/* QualifyCvesLauncher.js */

"use strict";

// One reusable on-demand schedule per tenant (the Get Support Data pattern):
// every launch reuses this TriggerType and fires it via runWorkNow(), so the
// Schedules area shows a single row whose History accumulates one execution
// per run — each with its work logs and per-device results.
const QualifyCvesOptionsComponent = require("./qualifycves/QualifyCvesOptionsComponent");

const ON_DEMAND_NAME = "Qualify CVE Exposure — On Demand";
// The on-demand launch runs the exposureRefresh ORCHESTRATOR, not bare
// qualifyCves: stale/missing dependencies (capture, taxonomy, support data)
// refresh first per the org policies, then qualification always runs. The
// "Refresh stale dependencies" dialog option opts a single run out.
const ON_DEMAND_WORK_TYPE = "exposureRefresh";

/**
 * Splits a comma-separated free-text field into a trimmed, empty-free list.
 * @param {String} raw Comma-separated user input.
 * @returns {String[]} Non-empty trimmed tokens (empty array when nothing entered).
 */
function parseIdList(raw) {
    if (!raw) {
        return [];
    }
    return String(raw)
        .split(",")
        .map((value) => value.trim())
        .filter((value) => value.length > 0);
}

/**
 * Maps the options-dialog control values into the qualifyCves work input.
 * Only meaningful keys are emitted, so an untouched dialog produces {} — the
 * pre-existing default-behavior payload.
 * @param {Object} values Raw control values from QualifyCvesOptionsRenderer.
 * @returns {Object} The workInput passed to setWorkInput().
 */
function buildQualifyCvesWorkInput(values) {
    values = values || {};
    const workInput = {};
    // Checkbox defaults ON; only the explicit opt-out is emitted so an
    // untouched dialog still produces the default-behavior payload.
    if (values.refreshDependencies === false) {
        workInput.skipDependencyRefresh = true;
    }
    // Two distinct scopes, not one flag with a hidden reach (backend R6).
    // reextractPredicates re-opens FAILED extractions only; reextractAllPredicates additionally
    // rewrites predicates that already succeeded on advisories whose text has not changed. The
    // second is genuinely risky — extraction is non-deterministic, so a re-read can return fewer
    // required feature tags and turn an Inconclusive verdict into Not Affected.
    if (values.reextractPredicates) {
        workInput.reextractPredicates = true;
    }
    if (values.reextractAllPredicates) {
        workInput.reextractAllPredicates = true;
    }
    if (values.forceRecompute) {
        workInput.forceRecompute = true;
    }
    if (values.runtimeDeepening && values.runtimeDeepening !== "default") {
        workInput.runtimeDeepening = values.runtimeDeepening;
    }
    const cveIds = parseIdList(values.cveIds);
    if (cveIds.length) {
        workInput.cveIds = cveIds;
    }
    const advisoryIds = parseIdList(values.advisoryIds);
    if (advisoryIds.length) {
        workInput.advisoryIds = advisoryIds;
    }
    return workInput;
}

/**
 * Runs the Device `qualifyCves` work through the shared on-demand schedule:
 * find-or-create the reusable SCHEDULE TriggerType, point its workTarget at the
 * current selection, apply the collected workInput, and fire it immediately via
 * runWorkNow(). The one-time occurrence is dated one minute in the PAST so the
 * schedule never self-fires — Run Now drives every execution. Status is written
 * only when it differs, and the schedule is written ONLY at create, because
 * patching either provokes a futures re-sync that races runWorkNow(). A trigger
 * that already exists therefore keeps the schedule it was created with: editing
 * the shape below will not reach any tenant that has launched at least once.
 * @param {Object} opts Same opts passed to launchQualifyCves.
 * @param {Object} workInput Launch options gathered from the dialog.
 * @returns {Promise}
 */
function runQualifyCves(opts, workInput) {
    const modalUserFeedback = opts.modalUserFeedback;
    const launchedAt = new Date();
    const occurrence = new Date(launchedAt.getTime() - 60 * 1000);
    const description =
        "On-demand runs. Last launched " +
        launchedAt.toLocaleDateString() +
        " " +
        launchedAt.toLocaleTimeString() +
        (opts.sourceLabel ? " from " + opts.sourceLabel : "") +
        ".";
    const schedule = {
        type: "one",
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        on: {
            year: occurrence.getFullYear(),
            month: occurrence.getMonth(),
            date: occurrence.getDate(),
            hour: occurrence.getHours(),
            minute: occurrence.getMinutes(),
        },
    };

    modalUserFeedback.displayLoading({
        message: "Launching Qualify CVE Exposure...",
        showHideButton: true,
    });

    return opts.TriggerTypeEntity.loadByQuery(
        opts.orgId,
        "TriggerType",
        // Keyed by displayName, NOT workType: rows created before the
        // orchestrator reroute say qualifyCves, and querying by the new
        // workType would orphan them while a same-named twin accumulates in
        // the Schedules area. The reuse path below patches the drift in place.
        {triggerType: "SCHEDULE", displayName: ON_DEMAND_NAME},
        {id: true, displayName: true, workType: true, status: true}
    )
        .then((existing) => {
            // DETERMINISTIC pick. Tenants that launched before the orchestrator reroute carry two
            // SCHEDULE rows under this one name — one still on the old workType — and taking [0]
            // meant the row we reused, and therefore whether the refresh stages ran at all, came
            // down to result order. Prefer a row already on the target workType so repeat launches
            // converge on the same one instead of alternating between twins.
            const candidates = existing || [];
            const found =
                candidates.filter((row) => row.workType === ON_DEMAND_WORK_TYPE)[0] ||
                candidates[0];
            let triggerType;
            if (found) {
                triggerType = found;
            } else {
                triggerType = new opts.TriggerTypeEntity(opts.orgId);
                triggerType.name = ON_DEMAND_NAME;
                triggerType
                    .setTriggerType("SCHEDULE")
                    .setSolution("Device Manager")
                    .setSubWorkType("CVE Qualification");
            }
            triggerType.description = description;
            triggerType
                .setWorkType(ON_DEMAND_WORK_TYPE)
                .setWorkTarget(opts.workTarget)
                .setWorkInput(workInput);
            if (!found) {
                triggerType.setSchedule(schedule);
            }
            if (triggerType.status !== "ACTIVE") {
                triggerType.setStatus("ACTIVE");
            }
            return found
                ? triggerType.update().then(() => triggerType)
                : triggerType.create().then(() => triggerType);
        })
        .then((triggerType) => {
            return triggerType.runWorkNow();
        })
        .then(() => {
            if (modalUserFeedback.isStillOpen) {
                return modalUserFeedback.displayNotification(
                    'Launched via the "' +
                        ON_DEMAND_NAME +
                        '" schedule.\r\nOpen it under Schedules to follow progress and view the run history, work logs, and per-device results; each device also logs the outcome in its Activity tab.',
                    {
                        title: "Qualify CVE Exposure Launched",
                        confirmDetails: {
                            text: "OK",
                            type: "text",
                        },
                    }
                );
            }
        })
        .then(() => {
            return modalUserFeedback.hideDisplayed();
        });
}

/**
 * Prompts for launch options, then launches the Device `qualifyCves` work with
 * the collected workInput. Cancelling the options dialog quietly aborts.
 *
 * @param {Object} opts
 * @param {Function} opts.TriggerTypeEntity TriggerType entity class ($teve.entities.TriggerType).
 * @param {String} opts.orgId Active organization id.
 * @param {Object} opts.workTarget Work target (query/multi shape) selecting the devices.
 * @param {Object} opts.modalUserFeedback $teve.ui.userFeedback.
 * @param {Object} opts.logger $teve.logger.
 * @param {String} [opts.sourceLabel] Launch origin recorded in the schedule description.
 * @returns {Promise} Resolves once the run is LAUNCHED (not when it completes) or the dialog is
 *   cancelled; never rejects — errors are surfaced through the modal.
 */
function launchQualifyCves(opts) {
    const modalUserFeedback = opts.modalUserFeedback;
    return modalUserFeedback
        .displayCustom(new QualifyCvesOptionsComponent(opts.logger), {dontHide: true})
        .then((values) => {
            return runQualifyCves(opts, buildQualifyCvesWorkInput(values));
        })
        .catch((err) => {
            if (err && err.userRejected === true) {
                // displayCustom ran with dontHide: true, so a cancel must
                // close the veil explicitly or it stays on screen.
                return modalUserFeedback.hideDisplayed();
            }
            opts.logger.warn({error: err}, "QualifyCvesLauncher - Error");
            if (err && err.errorCode === "NotAuthorized") {
                return modalUserFeedback.displayError({
                    errorMsg: 'User not authorized for "Qualify CVE Exposure"',
                    errorCode: "",
                });
            }
            if (err && (err.errorMsg || err.errorCode)) {
                return modalUserFeedback.displayError({error: err});
            }
            return modalUserFeedback.displayError({error: {errorMsg: err}});
        });
}

/**
 * Collect runtime evidence for ONE DEVICE, from the exposure drawer.
 *
 * DELIBERATELY NOT SCOPED TO ONE CVE. The backend unions every pending advisory's commands into a
 * single CLI session per device; scoping this to the clicked CVE would open a second session for
 * the next one, so a device with several unresolved CVEs would be asked to open several terminal
 * sessions in a row. One click therefore resolves every outstanding check on the box, and the
 * control says so rather than implying it only touches the row it sits on.
 *
 * forceRecompute is required: without it the run's early-exit skips any device whose signature is
 * unchanged, which is exactly this device — the config has not moved, only our willingness to ask
 * it a question has.
 *
 * No options dialog — but this DOES reach a production device, so the caller must confirm before
 * calling. `_buildRunCheckControl` does that in `_confirmDeviceChecks`, naming the device and the
 * scope. This header used to assert the confirmation existed while the only caller went from click
 * straight to launch, which is worse than saying nothing: it reads as a discharged obligation.
 * Anyone adding a second caller owes the same dialog.
 *
 * @param {Object} opts Same shape launchQualifyCves takes.
 * @returns {Promise} Resolves once the run is LAUNCHED, not when it completes.
 */
function launchRuntimeCheck(opts) {
    return runQualifyCves(opts, {
        runtimeDeepening: "on-demand",
        forceRecompute: true,
    }).catch((err) => {
        opts.logger.warn({error: err}, "QualifyCvesLauncher.launchRuntimeCheck - Error");
        if (err && err.errorCode === "NotAuthorized") {
            opts.modalUserFeedback.displayError({
                errorMsg: 'User not authorized for "Qualify CVE Exposure"',
                errorCode: "",
            });
        } else {
            opts.modalUserFeedback.displayError({error: err || {errorMsg: "Unknown error"}});
        }
        // RE-THROW. Returning the modal's result here RESOLVED the promise, so every caller's
        // success branch ran on failure: the drawer's run-now button reported "Checks running —
        // re-open this CVE for results" and stayed disabled next to an error dialog saying the
        // opposite, with no way to retry short of reopening the drawer. Displaying an error and
        // reporting success are not the same outcome and must not share an exit.
        throw err;
    });
}

module.exports = {launchQualifyCves, launchRuntimeCheck, buildQualifyCvesWorkInput};
